<template>
  <div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="customerUserDetailForm">
        <b-form
          ref="customerUserDetailFormElement"
          class=""
          aria-autocomplete="off"
          @submit.prevent="submit"
        >
          <b-row>
            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Name') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Name')" rules="required">
                  <b-form-input
                    id="name"
                    v-model="values.firstname"
                    name="name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Name')"
                    tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="surname">{{ t('Surname') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Surname')" rules="required">
                  <b-form-input
                    id="surname"
                    v-model="values.lastname"
                    name="surname"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Surname')"
                    tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="status">{{ t('Status') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Status')" rules="required">
                  <b-form-select
                    v-model="values.status"
                    :options="statusOptions"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Status')"
                    tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="role">{{ t('Role') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Role')" rules="required">
                  <b-form-select
                    id="role"
                    v-model="values.role"
                    :options="roleOptions"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Role')"
                    tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="password">{{ t('Password') }}</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Password')"
                  :rules="type === 'edit' ? '' : 'required'"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="password"
                      v-model="values.password"
                      :type="passwordFieldType"
                      class="form-control-merge"
                      :state="errors.length > 0 ? false : null"
                      name="login-password"
                      :placeholder="t('Password')"
                      tabindex="2"
                    />

                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="email">{{ t('Email') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Email')"
                  rules="required|email"
                >
                  <b-form-input
                    id="email"
                    v-model="values.email"
                    name="email"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Email')"
                    tabindex="8"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                tabindex="11"
              >
                {{
                  submitting
                    ? t('Please Wait')
                    : type === 'new'
                    ? t('Add New User')
                    : t('Edit User')
                }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BFormSelect,
  BButton,
  BOverlay
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { helper } from '@core/mixins/helper';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';

export default {
  components: {
    BOverlay,
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormSelect,

    ValidationObserver,
    ValidationProvider
  },

  mixins: [togglePasswordVisibility, helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          firstname: '',
          lastname: '',
          password: '',
          email: '',
          status: 0,
          role: ''
        };
      }
    }
  },
  data() {
    return {
      statusTypeOptions: []
    };
  },

  computed: {
    ...mapGetters({
      allRoles: 'app/allRoles'
    }),
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon';
    },

    statusOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.statusTypeOptions && this.statusTypeOptions.length > 0) {
        options.push(
          ...this.statusTypeOptions.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },
    roleOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.allRoles && this.allRoles.length > 0) {
        options.push(
          ...this.allRoles.map(r => ({
            text: r.description || r.name,
            value: r.name
          }))
        );
      }
      return options;
    }
  },

  async created() {
    this.statusTypeOptions = await this.getUserStatus();
    if (!this.allRoles || this.allRoles.length < 1) {
      this.fetchRoles();
    }
  },

  methods: {
    submit() {
      this.$refs.customerUserDetailForm.validate().then(success => {
        if (success) {
          const payload = { ...this.values };

          this.$emit('submit', {
            ...payload
          });
        }
      });
    },

    fetchRoles() {
      return useService.getRoles().then(response => {
        this.$store.commit('app/SET_ALL_ROLES', response.data);
      });
    },

    resetForm() {
      this.$refs.customerUserDetailFormElement.reset();
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
